import React from 'react';
import { Tooltip } from 'antd';
import { InfoIcon } from 'lucide-react';

import { QUESTION_VERSION_BADGE } from '../../../utils/staffFormValues';

/*
 * The "why is this question here?" marker, for a question that is not part of the version the
 * record was submitted on.
 *
 * Was a text pill rendered ABOVE the field by SectionCard. That put it in the normal flow of the
 * grid cell, so a badged question started ~23px lower than the un-badged one beside it and the
 * two-column grid visibly lost its alignment — worse when the pill wrapped to two lines. It now
 * rides inside the label instead, where it costs no vertical space at all and sits next to the
 * `(optional)` marker it belongs with.
 *
 * antd rather than react-bootstrap's OverlayTrigger, which is the other tooltip already in this
 * app: react-bootstrap is pinned at 1.6.8 — a Bootstrap 4 API — against bootstrap 5.3.6, and
 * BS5 renamed `.arrow` to `.tooltip-arrow`, so its arrow renders unstyled. antd is self-styled,
 * globally imported (index.css), and QuestionField already depends on it for DatePicker.
 *
 * Portal-rendered, which is load-bearing rather than incidental: both card components are
 * `tw-overflow-hidden`, so a CSS-only tooltip would be clipped at the card edge.
 *
 * `trigger` includes click because hover does not exist on touch, and a director reviewing
 * records on a tablet is a real case.
 *
 * Worded about the application rather than the reader: a director editing someone else's record
 * sees these too, and "New since you applied" would be false on their screen.
 */
const BADGE_TEXT = {
    [QUESTION_VERSION_BADGE.ADDED]: 'Added to the form after this application was submitted.',
    [QUESTION_VERSION_BADGE.REMOVED]: 'No longer on the form. It was answered when this application was submitted, so it stays editable here.',
};

/*
 * `text` overrides the badge lookup, for callers that carry their own wording — the read-only
 * record, whose rows are answers rather than questions and which knows things the form does not
 * (that a director filled this in later).
 */
const VersionHint = ({ badge, text, label = 'Why is this here?' }) => {
    const body = text || BADGE_TEXT[badge];
    if (!body) return null;

    return (
        <Tooltip title={body} trigger={['hover', 'click']} placement="top">
            <button
                type="button"
                aria-label={label}
                /*
                 * type="button" already keeps this out of form submission. preventDefault is for
                 * placement: callers render the hint next to a <label>, and a stray click landing
                 * inside one would otherwise toggle the control that label points at.
                 */
                onClick={event => event.preventDefault()}
                /*
                 * The button resets (border/background/font/padding) are spelled out rather than
                 * inherited from the scoped reset in staffApplication.tailwind.css: that reset is
                 * scoped to `.staff-application-page`, which only addCounselorNew and
                 * StaffFormBuilder set. This component also renders on the personnel record,
                 * which sits outside it — and there a bare <button> keeps its UA chrome, meaning
                 * a grey ButtonFace fill and an `outset` 3-D bevel instead of a flat icon.
                 */
                className="tw-ml-1.5 tw-inline-flex tw-shrink-0 tw-cursor-help tw-border-none tw-bg-transparent tw-p-0 tw-align-middle tw-font-sans tw-text-gray-400 tw-transition hover:tw-text-brand-600">
                <InfoIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
            </button>
        </Tooltip>
    );
};

export default VersionHint;
